// ============================================================
// HARMONIC CHARTER — Unified Parser Entry Point
// ============================================================
// Position model:
//   Each chord has a `position` — its beat offset from bar start.
//   Duration is implied by the gap to the next chord (or bar end).
//
// Hold tokens advance the cursor before the next chord:
//   .    = 1 beat   (quarter note)
//   ..   = 0.5 beat (eighth note)
//   ...  = 1/3 beat (triplet eighth)
//   .... = 0.25 beat (sixteenth note)
//
// Rest token:
//   -    = places an empty (silent) chord at the current cursor.
//          Takes up time like a chord but plays/displays nothing.
//          Example: "1 . - . 5" → I on beat 1, rest on beat 2, V on beat 3
//
// Algorithm:
//   cursor starts at 0.
//   chord token  → place chord at cursor, cursor stays put.
//   hold token   → advance cursor by hold value.
//   rest token   → place empty chord at cursor (same as chord token).
//   next chord   → placed at current cursor.
// ============================================================

import type {
  Chord,
  Key,
  TimeSignature,
  InputGrammar,
  ParseResult,
  BarParseResult,
} from '../types';
import { inferPositions } from '../theory';
import { isBorrowed } from '../analysis';
import { parseNashville, looksLikeNashville } from './nashville';
import { parseRoman, looksLikeRoman } from './roman';
import { parseChordName, looksLikeChordName } from './chordNames';

// ------------------------------------------------------------
// HOLD TOKEN DEFINITIONS
// ------------------------------------------------------------

export const HOLD_TOKENS: Array<{ token: string; beats: number }> = [
  { token: '....', beats: 0.25      },
  { token: '...',  beats: 1 / 3     },
  { token: '..',   beats: 0.5       },
  { token: '.',    beats: 1         },
];

export function isHoldToken(s: string): boolean {
  return HOLD_TOKENS.some((h) => h.token === s);
}

export function holdBeats(s: string): number {
  return HOLD_TOKENS.find((h) => h.token === s)?.beats ?? 1;
}

// ------------------------------------------------------------
// REST TOKEN
// A '-' in the input string means "silent beat / hold previous chord".
// It is placed at the current cursor position like a normal chord
// but produces an isEmpty:true chord so nothing plays or renders.
// ------------------------------------------------------------

export const REST_TOKEN = '-';

export function isRestToken(s: string): boolean {
  return s === REST_TOKEN;
}

/** Build an empty (rest) chord at the given position. */
function makeRestChord(position: number): Chord {
  return {
    degree:     1,
    accidental: null,
    quality:    'major',
    extensions: [],
    slash:      null,
    position,
    borrowed:   false,
    isEmpty:    true,
  };
}

// ------------------------------------------------------------
// GRAMMAR AUTO-DETECTION
// ------------------------------------------------------------

export function detectGrammar(token: string): InputGrammar {
  if (looksLikeNashville(token)) return 'nashville';
  if (looksLikeRoman(token))     return 'roman';
  if (looksLikeChordName(token)) return 'chordName';
  return 'nashville';
}

export function detectBarGrammar(tokens: string[]): InputGrammar {
  // Exclude hold and rest tokens from grammar detection
  const nonHold = tokens.filter(
    (t) => !isHoldToken(t) && !isRestToken(t) && t.trim() !== '',
  );
  if (nonHold.length === 0) return 'nashville';
  const counts: Record<InputGrammar, number> = { nashville: 0, roman: 0, chordName: 0 };
  for (const token of nonHold) counts[detectGrammar(token)]++;
  return (Object.entries(counts) as Array<[InputGrammar, number]>).reduce(
    (best, [g, count]) => (count > counts[best] ? g : best),
    'nashville' as InputGrammar,
  );
}

// ------------------------------------------------------------
// SINGLE TOKEN PARSER
// ------------------------------------------------------------

export function parseToken(token: string, key: Key, grammar?: InputGrammar): ParseResult {
  const g = grammar ?? detectGrammar(token);
  switch (g) {
    case 'nashville': return parseNashville(token);
    case 'roman':     return parseRoman(token);
    case 'chordName': return parseChordName(token, key);
  }
}

// ------------------------------------------------------------
// BAR STRING TOKENISER
// ------------------------------------------------------------

export function tokeniseBar(input: string): string[] {
  return input.trim().split(/\s+/).filter((t) => t.length > 0);
}

// ------------------------------------------------------------
// POSITION ASSIGNMENT
// ------------------------------------------------------------

function assignPositions(
  tokens: string[],
  timeNumerator: number,
): Array<{ token: string; position: number; isRest: boolean }> {
  // Treat rest tokens as chord-like for position purposes
  const hasHolds = tokens.some(isHoldToken);
  const chordTokens = tokens.filter((t) => !isHoldToken(t));

  if (chordTokens.length === 0) return [];

  // No holds — evenly spaced (rests count as chord slots)
  if (!hasHolds) {
    const positions = inferPositions(chordTokens.length, timeNumerator);
    return chordTokens.map((token, i) => ({
      token,
      position: positions[i],
      isRest: isRestToken(token),
    }));
  }

  // Walk tokens with a cursor
  const result: Array<{ token: string; position: number; isRest: boolean }> = [];
  let cursor = 0;

  for (const token of tokens) {
    if (isHoldToken(token)) {
      cursor += holdBeats(token);
    } else {
      result.push({
        token,
        position: parseFloat(cursor.toFixed(6)),
        isRest: isRestToken(token),
      });
    }
  }

  return result;
}

// ------------------------------------------------------------
// MAIN EXPORT: parseBar
// ------------------------------------------------------------

export function parseBar(
  input: string,
  timeSignature: TimeSignature,
  key: Key,
  grammar?: InputGrammar,
): BarParseResult {
  const tokens = tokeniseBar(input);
  if (tokens.length === 0) return { ok: true, chords: [] };

  const positioned = assignPositions(tokens, timeSignature.numerator);
  const errors: Array<{ token: string; error: string }> = [];
  const chords: Chord[] = [];

  for (const { token, position, isRest } of positioned) {
    // Rest token — emit a silent empty chord, no parsing needed
    if (isRest) {
      chords.push(makeRestChord(position));
      continue;
    }

    const g      = grammar ?? detectGrammar(token);
    const result = parseToken(token, key, g);
    if (result.ok) {
      chords.push({ ...result.chord, position });
    } else {
      errors.push({ token: result.raw, error: result.error });
    }
  }

  if (errors.length > 0) return { ok: false, errors };

  const chordsWithBorrowed = chords.map((chord) => ({
    ...chord,
    borrowed: chord.isEmpty ? false : isBorrowed(chord, key),
  }));

  return { ok: true, chords: chordsWithBorrowed };
}

// ------------------------------------------------------------
// RE-EXPORTS
// ------------------------------------------------------------

export { parseNashville } from './nashville';
export { parseRoman }     from './roman';
export { parseChordName } from './chordNames';
export { HOLD_TOKEN }     from './nashville';